import { useMemo } from 'react'
import styled from 'styled-components'
import { Layer, Space, Time } from '~/packages/general/variable/spaceTime.types'
import { VariableAndOptionalResult } from '~/packages/general/variable/variables.typing'
import useAction from '~/prix/react/hooks/action'
import actionDefinitions from '../actionDefinitions'
import chroma from 'chroma-js'
import { colorMaps, institutionalColors, organizationalColors } from '~/design'
import { formatAsBrNumber, fromUnknownError, mapRange, truthy } from '~/prix'
import quantile from '~/prix/utils/math/quantile'
import { isEmpty } from '~/prix/utils/empty'
import PulseLoaderComponent from '~/prix/react/components/pulseLoader'
import { buildOptionalStatsActionInput } from './statsActionOptionalInput.util'
import { ClusterListLayer, isVariableLayer } from './clusterListInput.util'

const ModalLayout = styled.article`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  border-radius: 20px;
  background: ${institutionalColors.brand97};
  box-shadow: 0 24px 64px rgba(12, 35, 64, 0.18);
`

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px 24px 20px;
  background:
    linear-gradient(
      135deg,
      ${institutionalColors.brand20} 0%,
      ${institutionalColors.brand40} 58%,
      ${organizationalColors.aquaBlue30} 100%
    );
  color: #fff;

  @media screen and (max-width: 768px) {
    padding: 20px 16px 16px;
  }
`

const HeaderEyebrow = styled.span`
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const HeaderTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;

  h2 {
    margin: 0;
    font-size: 1.75rem;
    line-height: 1.05;

    @media screen and (max-width: 768px) {
      font-size: 1.35rem;
    }
  }
`

const HeaderContext = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.84);
  font-size: 0.95rem;
  line-height: 1.35;
`

const HeaderCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 0.82rem;
  font-weight: 600;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background:
    radial-gradient(circle at top right, rgba(20, 173, 204, 0.08), transparent 24%),
    linear-gradient(180deg, rgba(15, 67, 138, 0.05) 0%, #ffffff 22%);
`

const SummaryPanel = styled.section`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 24px 16px;
  border-bottom: 1px solid ${institutionalColors.neutral90};
  background: linear-gradient(180deg, #fff 0%, ${institutionalColors.neutral97} 100%);

  @media screen and (max-width: 768px) {
    padding: 16px;
  }
`

const SummaryTitle = styled.span`
  color: ${institutionalColors.neutral45};
  font-size: 0.82rem;
  line-height: 1.4;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border: 1px solid ${institutionalColors.neutral90};
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.94);
`

const SummaryCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const SummaryCardTitle = styled.h3`
  margin: 0;
  color: ${institutionalColors.brand20};
  font-size: 0.92rem;
  font-weight: 700;
`

const SummaryCardCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  border: 1px solid ${institutionalColors.neutral90};
  color: ${institutionalColors.neutral30};
  font-size: 0.78rem;
  font-weight: 700;
`

const SummaryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const SummaryChip = styled.span<{ $tone: 'up' | 'down' | 'more' }>`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.34rem 0.7rem;
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === 'up'
      ? 'rgba(39, 174, 96, 0.12)'
      : $tone === 'down'
        ? 'rgba(231, 76, 60, 0.12)'
        : 'rgba(57, 77, 96, 0.08)'};
  color: ${({ $tone }) =>
    $tone === 'up'
      ? '#1c7a47'
      : $tone === 'down'
        ? '#b63a2b'
        : institutionalColors.neutral30};
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.3;
`

const SummaryEmpty = styled.span`
  color: ${institutionalColors.neutral45};
  font-size: 0.84rem;
`

const ScrollableContent = styled.main`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;

  @media screen and (max-width: 768px) {
    padding: 16px;
  }
`

const CategoryCard = styled.section`
  display: flex;
  flex-direction: column;
  border: 1px solid ${institutionalColors.neutral90};
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 10px 28px rgba(20, 46, 82, 0.08);
  overflow: hidden;
`

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid ${institutionalColors.neutral90};

  h3 {
    margin: 0;
    color: ${institutionalColors.brand20};
    font-size: 1rem;
  }
`

const CategoryCount = styled.span`
  color: ${institutionalColors.neutral45};
  font-size: 0.8rem;
  font-weight: 600;
`

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`

const OptionCard = styled.article<{ $isHighlighted: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid
    ${({ $isHighlighted }) =>
      $isHighlighted ? institutionalColors.brand75 : institutionalColors.neutral90};
  background:
    ${({ $isHighlighted }) =>
      $isHighlighted
        ? 'linear-gradient(180deg, rgba(92, 143, 214, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
        : '#fff'};
`

const OptionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  h4 {
    margin: 0;
    color: ${institutionalColors.neutral20};
    font-size: 0.96rem;
    line-height: 1.35;
  }
`

const OptionBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`

const OptionBadge = styled.span<{ $tone: 'up' | 'down' | 'neutral' | 'highlight' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.62rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ $tone }) =>
    $tone === 'up'
      ? 'rgba(39, 174, 96, 0.14)'
      : $tone === 'down'
        ? 'rgba(231, 76, 60, 0.14)'
        : $tone === 'highlight'
          ? 'rgba(15, 67, 138, 0.12)'
          : 'rgba(57, 77, 96, 0.08)'};
  color: ${({ $tone }) =>
    $tone === 'up'
      ? '#1c7a47'
      : $tone === 'down'
        ? '#b63a2b'
        : $tone === 'highlight'
          ? institutionalColors.brand20
          : institutionalColors.neutral30};
`

const TrackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const TrackRow = styled.div`
  display: grid;
  grid-template-columns: 7.5rem minmax(0, 1fr) 5.5rem;
  align-items: center;
  gap: 12px;

  @media screen and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`

const TrackLabel = styled.span`
  color: ${institutionalColors.neutral45};
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.25;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const TrackBar = styled.div`
  min-width: 0;
  width: 100%;
  height: 12px;
  overflow: hidden;
  border-radius: 999px;
  background: ${institutionalColors.neutral90};
`

const TrackFill = styled.div<{ $width: number; $color: string }>`
  width: ${({ $width }) => `${$width}%`};
  max-width: 100%;
  height: 100%;
  border-radius: inherit;
  background: ${({ $color }) => $color};
`

const TrackValue = styled.span`
  color: ${institutionalColors.neutral20};
  font-size: 0.84rem;
  font-weight: 700;
  text-align: right;
`

interface SelfComparisonProps {
  timeRange?: {
    start: string
    end: string
  }
  spaceDelimitationId?: string | number
  spaceDelimitationType?: string
  layer: Layer
  normalLabel?: string
  foundLabel?: string
}

type CategorizedValues = Array<{
  key: string
  name: string
  options: Array<{
    key: string
    name: string
    percentage: number
  }>
}>

type CategorizedValuesWithNormalized = Array<{
  key: string
  name: string
  options: Array<{
    key: string
    name: string
    percentage: number
    normalized: number
  }>
}>

type AssertionEntry = NonNullable<VariableAndOptionalResult['assertions']>[string]

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function getShapeFields(layer: ClusterListLayer): Record<string, unknown> | undefined {
  const shape = layer.variable.shape
  if (!isRecord(shape) || !isRecord(shape.shape)) {
    return undefined
  }

  return shape.shape as Record<string, unknown>
}

function getFieldLabel(field: unknown) {
  if (!isRecord(field)) {
    return undefined
  }

  if (typeof field.label === 'string') {
    return field.label
  }

  if (isRecord(field.currentMeta) && typeof field.currentMeta.label === 'string') {
    return field.currentMeta.label
  }

  return undefined
}

function getFieldKeysAndLabels(field: unknown) {
  if (!isRecord(field)) {
    return undefined
  }

  const candidate =
    (isRecord(field.meta) ? field.meta.keysAndLabels : undefined) ??
    (isRecord(field.currentMeta) ? field.currentMeta.keysAndLabels : undefined) ??
    field.keysAndLabels

  if (!isRecord(candidate)) {
    return undefined
  }

  const entries = Object.entries(candidate).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  )

  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

function resolveAssertionLabel(layer: ClusterListLayer, assertion: AssertionEntry) {
  const expression = assertion.expression
  if (expression.kind !== 'binary') {
    return assertion.label
  }

  if (expression.left.kind !== 'property' || expression.right.kind !== 'literal') {
    return assertion.label
  }

  const property = String(expression.left.property)
  const field = getShapeFields(layer)?.[property]
  const fieldLabel = getFieldLabel(field) ?? property

  if (expression.right.literalType === 'string') {
    const value = String(expression.right.value)
    const enumLabel = getFieldKeysAndLabels(field)?.[value]
    if (enumLabel) {
      return `${enumLabel} (${fieldLabel})`
    }
  }

  if (expression.right.literalType === 'boolean' && expression.right.value === true) {
    return fieldLabel
  }

  return assertion.label
}

function subtractPercentages(
  normal: CategorizedValues,
  found: CategorizedValues,
): CategorizedValuesWithNormalized {
  return found.map((foundCategory, index) => {
    const normalCategory = normal.find(normalCategory => normalCategory.key === foundCategory.key)
    if (!normalCategory) {
      throw new Error(`Category ${foundCategory.key} not found in normal`)
    }
    const options = foundCategory.options.map((foundOption, optionIndex) => {
      const normalOption = normalCategory.options.find(
        normalOption => normalOption.key === foundOption.key,
      )
      if (!normalOption) {
        throw new Error(`Option ${foundOption.key} not found in normal`)
      }
      const subtracted = foundOption.percentage - normalOption.percentage
      const max = Math.max(foundOption.percentage, normalOption.percentage)
      return {
        ...foundOption,
        percentage: foundOption.percentage - normalOption.percentage,
        normalized: subtracted === 0 && max === 0 ? 0 : subtracted / max,
      }
    })

    return {
      ...foundCategory,
      options,
    }
  })
}

function formatPercentage(value: number) {
  return `${formatAsBrNumber(value * 100, 2)}%`
}

function formatPercentagePoints(value: number) {
  if (value === 0) return '0 p.p.'

  const sign = value > 0 ? '+' : '-'
  return `${sign}${formatAsBrNumber(Math.abs(value) * 100, 2)} p.p.`
}

function getBarWidth(value: number) {
  if (!Number.isFinite(value) || value <= 0) return 0

  const percentage = Math.max(value * 100, 0)
  return Math.min(100, percentage < 2 ? 2 : percentage)
}

export default function SelfComparisonReport({
  layer,
  spaceDelimitationId,
  spaceDelimitationType,
  timeRange,
  normalLabel = 'Normal',
  foundLabel = 'Anomalia',
}: SelfComparisonProps) {
  if (!isVariableLayer(layer)) {
    return <output>Comparação indisponível para essa camada.</output>
  }

  const variableLayer = layer
  const { variable } = variableLayer
  const assertions = variable.assertions

  if (!assertions) {
    throw new Error(`Variable ${variable.key} does not have assertions`)
  }

  const actionDefinition = useMemo(() => {
    const actionDefinition = actionDefinitions.find(
      actionDefinition => actionDefinition.key === variable.actionDefinitionKey,
    )
    if (!actionDefinition) {
      throw new Error(`Action definition not found for variable ${variable.key}`)
    }

    return actionDefinition
  }, [variable.actionDefinitionKey, variable.key])

  const normalInput = useMemo(() => {
    return {
      variable,
      assertions,
      ...buildOptionalStatsActionInput({
        filter: variable.filter,
        highlight: variable.highlight,
      }),
    }
  }, [assertions, variable])
  const normalResult = useAction(actionDefinition, normalInput)

  const foundInput = useMemo(() => {
    const space =
      spaceDelimitationType && !isEmpty(spaceDelimitationId)
        ? ({
            delimitationId: spaceDelimitationId,
            delimitationType: spaceDelimitationType,
          } as Space)
        : undefined
    const time = timeRange
      ? ({
          type: 'customDateRange',
          startDate: timeRange.start,
          endDate: timeRange.end,
        } as Time)
      : undefined

    return {
      variable,
      assertions,
      ...buildOptionalStatsActionInput({
        filter: variable.filter,
        highlight: variable.highlight,
        space,
        time,
      }),
    }
  }, [assertions, spaceDelimitationId, spaceDelimitationType, timeRange, variable])
  const foundResult = useAction(actionDefinition, foundInput)

  const assertionLabels = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(assertions).map(([key, assertion]) => [
          key,
          resolveAssertionLabel(variableLayer, assertion),
        ]),
      ),
    [assertions, variable.shape, variableLayer],
  )

  const result = useMemo(() => {
    if (!foundResult.result || !normalResult.result) {
      return
    }

    const normal = [
      {
        key: '-',
        name: variable.title,
        options: Object.entries(assertions).map(([key, assertion]) => ({
          key,
          name: assertionLabels[key] ?? assertion.label,
          percentage:
            normalResult.result.items[0][key] === 0 && normalResult.result.items[0].count === 0
              ? 0
              : normalResult.result.items[0][key] / normalResult.result.items[0].count,
        })),
      },
    ]

    const found = [
      {
        key: '-',
        name: variable.title,
        options: Object.entries(assertions).map(([key, assertion]) => ({
          key,
          name: assertionLabels[key] ?? assertion.label,
          percentage:
            foundResult.result.items[0][key] === 0 && foundResult.result.items[0].count === 0
              ? 0
              : foundResult.result.items[0][key] / foundResult.result.items[0].count,
        })),
      },
    ]

    const subtracted = subtractPercentages(normal, found)

    const normalizedValues = subtracted
      .map(category => category.options.map(option => option.normalized))
      .flat()

    const someNormalizedIsNotFinite = normalizedValues.some(value => !isFinite(value))
    if (someNormalizedIsNotFinite) {
      throw new Error('Some normalized values are not finite')
    }

    const maxNormalized = Math.max(...normalizedValues)
    const minNormalized = Math.min(...normalizedValues)
    const colorScale = chroma.scale(colorMaps.toHeatNeutralMiddle).mode('lrgb')
    const scaleColorFunction = (normalized: number) =>
      maxNormalized === minNormalized
        ? colorScale(0.5).hex()
        : colorScale(mapRange(normalized, minNormalized, maxNormalized, 0, 1)).hex()

    const quantileThreshold = 0.05
    const lowerQuantile = quantile(normalizedValues, quantileThreshold)
    const upperQuantile = quantile(normalizedValues, 1 - quantileThreshold)

    const lowerItems = subtracted
      .map(category =>
        category.options.map(option => {
          if (option.normalized <= lowerQuantile) {
            return {
              label: `${option.name} (${category.name})`,
              normalized: option.normalized,
            }
          }
          return null
        }),
      )
      .flat()
      .filter(truthy)
      .sort((a, b) => a.normalized - b.normalized)
      .map(item => item!.label)
    const upperItems = subtracted
      .map(category =>
        category.options.map(option => {
          if (option.normalized >= upperQuantile) {
            return {
              label: `${option.name} (${category.name})`,
              normalized: option.normalized,
            }
          }
          return null
        }),
      )
      .flat()
      .filter(truthy)
      .sort((a, b) => b.normalized - a.normalized)
      .map(item => item!.label)

    return {
      normal,
      found,
      subtracted,
      lowerItems,
      upperItems,
      lowerQuantile,
      upperQuantile,
      scaleColorFunction,
    }
  }, [
    assertionLabels,
    assertions,
    foundResult.result,
    variable.title,
    normalResult.result,
  ])

  if (foundResult.error || normalResult.error) {
    return <output>{fromUnknownError(foundResult.error! || normalResult.error!).message}</output>
  }

  if (foundResult.isLoading || normalResult.isLoading) {
    return <PulseLoaderComponent />
  }

  if (!result) {
    return null
  }

  const {
    normal,
    found,
    subtracted,
    lowerItems,
    upperItems,
    lowerQuantile,
    upperQuantile,
    scaleColorFunction,
  } = result
  const totalCompared = subtracted.reduce((sum, category) => sum + category.options.length, 0)
  const highlightedIncreaseItems = upperItems.slice(0, 6)
  const highlightedDecreaseItems = lowerItems.slice(0, 6)
  const hiddenIncreaseItemsCount = Math.max(upperItems.length - highlightedIncreaseItems.length, 0)
  const hiddenDecreaseItemsCount = Math.max(lowerItems.length - highlightedDecreaseItems.length, 0)

  return (
    <ModalLayout>
      <Header>
        <HeaderEyebrow>Comparar recorte</HeaderEyebrow>
        <HeaderTitle>
          <h2>Comparação das características</h2>
          <HeaderCount>{totalCompared} características</HeaderCount>
        </HeaderTitle>
        <HeaderContext>
          Comparando <strong>{foundLabel}</strong> com <strong>{normalLabel}</strong> em{' '}
          {variable.title}.
        </HeaderContext>
      </Header>

      <Body>
        <SummaryPanel>
          <SummaryTitle>
            Os destaques abaixo mostram os grupos com maior aumento ou redução proporcional no
            recorte selecionado.
          </SummaryTitle>
          <SummaryGrid>
            <SummaryCard>
              <SummaryCardHeader>
                <SummaryCardTitle>Proporção aumentada</SummaryCardTitle>
                <SummaryCardCount>{upperItems.length}</SummaryCardCount>
              </SummaryCardHeader>
              {highlightedIncreaseItems.length > 0 ? (
                <SummaryChips>
                  {highlightedIncreaseItems.map(item => (
                    <SummaryChip key={item} $tone='up'>
                      {item}
                    </SummaryChip>
                  ))}
                  {hiddenIncreaseItemsCount > 0 ? (
                    <SummaryChip $tone='more'>+{hiddenIncreaseItemsCount}</SummaryChip>
                  ) : null}
                </SummaryChips>
              ) : (
                <SummaryEmpty>Nenhum aumento relevante encontrado.</SummaryEmpty>
              )}
            </SummaryCard>

            <SummaryCard>
              <SummaryCardHeader>
                <SummaryCardTitle>Proporção diminuída</SummaryCardTitle>
                <SummaryCardCount>{lowerItems.length}</SummaryCardCount>
              </SummaryCardHeader>
              {highlightedDecreaseItems.length > 0 ? (
                <SummaryChips>
                  {highlightedDecreaseItems.map(item => (
                    <SummaryChip key={item} $tone='down'>
                      {item}
                    </SummaryChip>
                  ))}
                  {hiddenDecreaseItemsCount > 0 ? (
                    <SummaryChip $tone='more'>+{hiddenDecreaseItemsCount}</SummaryChip>
                  ) : null}
                </SummaryChips>
              ) : (
                <SummaryEmpty>Nenhuma redução relevante encontrada.</SummaryEmpty>
              )}
            </SummaryCard>
          </SummaryGrid>
        </SummaryPanel>

        <ScrollableContent>
          {subtracted.map(category => {
            const normalCategory = normal.find(
              normalCategory => normalCategory.key === category.key,
            )!
            const foundCategory = found.find(foundCategory => foundCategory.key === category.key)!

            return (
              <CategoryCard key={category.key}>
                <CategoryHeader>
                  <h3>{category.name}</h3>
                  <CategoryCount>{category.options.length} grupos comparados</CategoryCount>
                </CategoryHeader>

                <OptionList>
                  {category.options.map(option => {
                    const normalOption = normalCategory.options.find(
                      normalOption => normalOption.key === option.key,
                    )!
                    const foundOption = foundCategory.options.find(
                      foundOption => foundOption.key === option.key,
                    )!
                    const color = scaleColorFunction(option.normalized)
                    const difference = foundOption.percentage - normalOption.percentage
                    const differenceTone =
                      difference > 0 ? 'up' : difference < 0 ? 'down' : 'neutral'
                    const isHighlighted =
                      option.normalized <= lowerQuantile || option.normalized >= upperQuantile

                    return (
                      <OptionCard key={option.key} $isHighlighted={isHighlighted}>
                        <OptionHeader>
                          <h4>{option.name}</h4>
                          <OptionBadges>
                            {isHighlighted ? (
                              <OptionBadge $tone='highlight'>Destaque</OptionBadge>
                            ) : null}
                            <OptionBadge $tone={differenceTone}>
                              {formatPercentagePoints(difference)}
                            </OptionBadge>
                          </OptionBadges>
                        </OptionHeader>

                        <TrackList>
                          <TrackRow>
                            <TrackLabel>{normalLabel}</TrackLabel>
                            <TrackBar title={formatPercentage(normalOption.percentage)}>
                              <TrackFill
                                $width={getBarWidth(normalOption.percentage)}
                                $color={institutionalColors.neutral60}
                              />
                            </TrackBar>
                            <TrackValue>{formatPercentage(normalOption.percentage)}</TrackValue>
                          </TrackRow>

                          <TrackRow>
                            <TrackLabel>{foundLabel}</TrackLabel>
                            <TrackBar title={formatPercentage(foundOption.percentage)}>
                              <TrackFill
                                $width={getBarWidth(foundOption.percentage)}
                                $color={color}
                              />
                            </TrackBar>
                            <TrackValue>{formatPercentage(foundOption.percentage)}</TrackValue>
                          </TrackRow>
                        </TrackList>
                      </OptionCard>
                    )
                  })}
                </OptionList>
              </CategoryCard>
            )
          })}
        </ScrollableContent>
      </Body>
    </ModalLayout>
  )
}
